import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { Location } from '@angular/common';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { OrdersService } from '../../services/orders.service';
import { GlobalSearchService } from '../../services/globalsearchservice.service';

@Component({
	selector: 'app-status-maintenance',
	templateUrl: './status-maintenance.component.html',
	styleUrls: ['./status-maintenance.component.scss']
})
export class StatusMaintenanceComponent implements OnInit {
	private readonly defaultPickerHex = '#6c757d';

	color: any = 'blue';
	orderRows: any[] = [];
	poRows: any[] = [];
	orderForm: UntypedFormGroup;
	poForm: UntypedFormGroup;
	orderEditingNew = false;
	poEditingNew = false;
	loadingOrder = false;
	loadingPo = false;

	@ViewChild('orderStatusEdit') orderStatusEditRef: ElementRef;
	@ViewChild('poStatusEdit') poStatusEditRef: ElementRef;

	constructor(
		private ordersService: OrdersService,
		private fb: UntypedFormBuilder,
		private location: Location,
		private globalSearchService: GlobalSearchService,
		private modalService: NgbModal
	) {
		this.orderForm = this.fb.group({
			order_status_id: [''],
			language_id: [1, Validators.required],
			name: ['', Validators.required],
			isdispatch: [false],
			badge_bg: [''],
			badge_fg: [''],
		});
		this.poForm = this.fb.group({
			status_id: [''],
			name: ['', Validators.required],
			color: [''],
			badge_fg: [''],
			receive: [1, Validators.required],
			print: [1, Validators.required],
			pocompleted: [false],
			summary: [false],
		});
	}

	ngOnInit(): void {
		this.color = this.globalSearchService.getColor();
		this.reloadOrders();
		this.reloadPo();
	}

	back(): void {
		this.location.back();
	}

	/** Value for &lt;input type="color"&gt; (#rrggbb). */
	orderHex(control: string): string {
		return this.cssColorToHexForPicker(this.orderForm.get(control)?.value);
	}

	poHex(control: string): string {
		return this.cssColorToHexForPicker(this.poForm.get(control)?.value);
	}

	onPickerInput(form: UntypedFormGroup, control: string, ev: Event): void {
		const hex = (ev.target as HTMLInputElement).value;
		form.get(control)?.setValue(hex);
	}

	private cssColorToHexForPicker(raw: string | null | undefined): string {
		const s = (raw ?? '').trim();
		if (!s) {
			return this.defaultPickerHex;
		}
		const fromHex = this.tryParseHexString(s);
		if (fromHex) {
			return fromHex;
		}
		const fromRgb = this.tryRgbStringToHex(s);
		if (fromRgb) {
			return fromRgb;
		}
		if (typeof document !== 'undefined') {
			const fromNamed = this.colorToHexViaComputed(s);
			if (fromNamed) {
				return fromNamed;
			}
		}
		return this.defaultPickerHex;
	}

	private tryParseHexString(s: string): string | null {
		if (/^#([0-9a-f]{6})$/i.test(s)) {
			return s.toLowerCase();
		}
		const m3 = s.match(/^#([0-9a-f]{3})$/i);
		if (m3) {
			const [, h] = m3;
			return `#${h[0]}${h[0]}${h[1]}${h[1]}${h[2]}${h[2]}`.toLowerCase();
		}
		return null;
	}

	private tryRgbStringToHex(s: string): string | null {
		const m = s.match(/^rgba?\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})/i);
		if (!m) {
			return null;
		}
		const clamp = (n: number) => Math.max(0, Math.min(255, n));
		const toH = (x: string) => clamp(parseInt(x, 10)).toString(16).padStart(2, '0');
		return `#${toH(m[1])}${toH(m[2])}${toH(m[3])}`;
	}

	private colorToHexViaComputed(value: string): string | null {
		try {
			const el = document.createElement('div');
			el.style.position = 'absolute';
			el.style.visibility = 'hidden';
			el.style.left = '-9999px';
			el.style.color = value;
			document.body.appendChild(el);
			const rgb = getComputedStyle(el).color;
			document.body.removeChild(el);
			return this.tryRgbStringToHex(rgb);
		} catch {
			return null;
		}
	}

	private openOrderModal(): void {
		this.modalService.open(this.orderStatusEditRef, {
			ariaLabelledBy: 'modal-title',
			size: 'lg',
			centered: true,
			scrollable: true,
		});
	}

	private openPoModal(): void {
		this.modalService.open(this.poStatusEditRef, {
			ariaLabelledBy: 'modal-title-po',
			size: 'lg',
			centered: true,
			scrollable: true,
		});
	}

	reloadOrders() {
		this.loadingOrder = true;
		this.ordersService.getOrderStatusMaster().subscribe({
			next: (r: any) => {
				this.orderRows = r && r.rows ? r.rows : [];
				this.loadingOrder = false;
			},
			error: () => {
				this.loadingOrder = false;
				this.globalSearchService.showNotification('Failed to load order statuses', 'danger', 'bottom', 'right');
			}
		});
	}

	reloadPo() {
		this.loadingPo = true;
		this.ordersService.getPoStatusMaster().subscribe({
			next: (r: any) => {
				this.poRows = r && r.rows ? r.rows : [];
				this.loadingPo = false;
			},
			error: () => {
				this.loadingPo = false;
				this.globalSearchService.showNotification('Failed to load PO statuses', 'danger', 'bottom', 'right');
			}
		});
	}

	newOrderRow() {
		this.orderEditingNew = true;
		this.orderForm.reset({
			order_status_id: '',
			language_id: 1,
			name: '',
			isdispatch: false,
			badge_bg: '',
			badge_fg: '',
		});
		this.openOrderModal();
	}

	editOrderRow(row: any) {
		this.orderEditingNew = false;
		this.orderForm.patchValue({
			order_status_id: row.order_status_id,
			language_id: row.language_id,
			name: row.name,
			isdispatch: !!Number(row.isdispatch),
			badge_bg: row.badge_bg || '',
			badge_fg: row.badge_fg || '',
		});
		this.openOrderModal();
	}

	saveOrderRow() {
		if (this.orderForm.invalid) {
			return;
		}
		const v = this.orderForm.value;
		const payload: any = {
			name: v.name,
			language_id: Number(v.language_id),
			isdispatch: v.isdispatch ? 1 : 0,
			badge_bg: v.badge_bg || '',
			badge_fg: v.badge_fg || '',
		};
		if (this.orderEditingNew) {
			payload.is_new = true;
		} else {
			payload.order_status_id = Number(v.order_status_id);
		}
		this.ordersService.saveOrderStatusMaster(payload).subscribe({
			next: (r: any) => {
				if (r && r.success === false) {
					this.globalSearchService.showNotification(r.error || 'Save failed', 'danger', 'bottom', 'right');
					return;
				}
				if (r && r.rows) {
					this.orderRows = r.rows;
				} else {
					this.reloadOrders();
				}
				this.modalService.dismissAll();
				this.resetOrderForm();
			},
			error: () => {
				this.globalSearchService.showNotification('Save failed', 'danger', 'bottom', 'right');
			}
		});
	}

	deleteOrderRow(row: any) {
		if (!confirm('Delete this order status row?')) {
			return;
		}
		this.ordersService.deleteOrderStatusMaster({
			order_status_id: row.order_status_id,
			language_id: row.language_id,
		}).subscribe({
			next: (r: any) => {
				if (r && r.success === false) {
					const msg = r.error === 'in_use' ? 'Status is in use on orders' : (r.error || 'Delete failed');
					this.globalSearchService.showNotification(msg, 'danger', 'bottom', 'right');
					return;
				}
				this.reloadOrders();
			},
			error: () => {
				this.globalSearchService.showNotification('Delete failed', 'danger', 'bottom', 'right');
			}
		});
	}

	resetOrderForm() {
		this.orderForm.reset({
			order_status_id: '',
			language_id: 1,
			name: '',
			isdispatch: false,
			badge_bg: '',
			badge_fg: '',
		});
	}

	cancelOrderEdit() {
		this.resetOrderForm();
		this.modalService.dismissAll();
	}

	newPoRow() {
		this.poEditingNew = true;
		this.poForm.reset({
			status_id: '',
			name: '',
			color: '',
			badge_fg: '',
			receive: 1,
			print: 1,
			pocompleted: false,
			summary: false,
		});
		this.openPoModal();
	}

	editPoRow(row: any) {
		this.poEditingNew = false;
		this.poForm.patchValue({
			status_id: row.status_id,
			name: row.name,
			color: row.color || '',
			badge_fg: row.badge_fg || '',
			receive: Number(row.receive),
			print: Number(row.print),
			pocompleted: !!Number(row.pocompleted),
			summary: !!Number(row.summary),
		});
		this.openPoModal();
	}

	savePoRow() {
		if (this.poForm.invalid) {
			return;
		}
		const v = this.poForm.value;
		const payload: any = {
			name: v.name,
			color: v.color || '',
			badge_fg: v.badge_fg || '',
			receive: Number(v.receive),
			print: Number(v.print),
			pocompleted: v.pocompleted ? 1 : 0,
			summary: v.summary ? 1 : 0,
		};
		if (this.poEditingNew) {
			payload.is_new = true;
		} else {
			payload.status_id = Number(v.status_id);
		}
		this.ordersService.savePoStatusMaster(payload).subscribe({
			next: (r: any) => {
				if (r && r.success === false) {
					this.globalSearchService.showNotification(r.error || 'Save failed', 'danger', 'bottom', 'right');
					return;
				}
				if (r && r.rows) {
					this.poRows = r.rows;
				} else {
					this.reloadPo();
				}
				this.modalService.dismissAll();
				this.resetPoForm();
			},
			error: () => {
				this.globalSearchService.showNotification('Save failed', 'danger', 'bottom', 'right');
			}
		});
	}

	deletePoRow(row: any) {
		if (!confirm('Delete this PO status?')) {
			return;
		}
		this.ordersService.deletePoStatusMaster({ status_id: row.status_id }).subscribe({
			next: (r: any) => {
				if (r && r.success === false) {
					const msg = r.error === 'in_use' ? 'Status is in use on POs' : (r.error || 'Delete failed');
					this.globalSearchService.showNotification(msg, 'danger', 'bottom', 'right');
					return;
				}
				this.reloadPo();
			},
			error: () => {
				this.globalSearchService.showNotification('Delete failed', 'danger', 'bottom', 'right');
			}
		});
	}

	resetPoForm() {
		this.poForm.reset({
			status_id: '',
			name: '',
			color: '',
			badge_fg: '',
			receive: 1,
			print: 1,
			pocompleted: false,
			summary: false,
		});
	}

	cancelPoEdit() {
		this.resetPoForm();
		this.modalService.dismissAll();
	}
}
